import { useCallback, useEffect, useState } from 'react';
import { getCompetitionTeams, createCompetitionTeam } from '../../services/api';
import TeamView from './TeamView';

// Competition teams. The academy's is called Legacy — the horse mascot behind
// the product name — but the screen takes more than one, because a second team
// is the obvious next thing to ask for.

const FIELD = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

export default function Competition() {
  const [teams, setTeams] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [season, setSeason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCompetitionTeams();
      setTeams(data.teams);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const { data } = await createCompetitionTeam({ name: name.trim(), season: season.trim() });
      setCreating(false);
      setName('');
      setSeason('');
      await load();
      // A new team is empty; picking who is on it is the only useful next step.
      setOpenId(data.team.id);
    } catch (createError) {
      setError(createError.response?.data?.error || 'Could not create that team');
    }
  };

  if (openId) {
    return <TeamView teamId={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Competition teams</h2>
        <button
          onClick={() => setCreating((c) => !c)}
          className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700"
        >
          {creating ? 'Cancel' : '+ New team'}
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Legacy"
              className={`${FIELD} flex-1`}
              autoFocus
            />
            <input
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              placeholder="2026 season"
              className={FIELD}
            />
            <button
              type="submit"
              disabled={!name.trim()}
              className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      )}

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="space-y-2">
        {teams.map((team) => (
          <button
            key={team.id}
            onClick={() => setOpenId(team.id)}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm"
          >
            <div className="min-w-0">
              <div className="truncate font-semibold text-gray-900">{team.name}</div>
              <div className="text-sm text-gray-500">
                {team.season || 'No season set'} · {team.memberCount} dancer{team.memberCount === 1 ? '' : 's'}
                {team.sessions > 0 && ` · ${team.sessions} session${team.sessions === 1 ? '' : 's'} recorded`}
              </div>
            </div>
            {!team.active && (
              <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                Inactive
              </span>
            )}
          </button>
        ))}
      </div>

      {!loading && teams.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center">
          <p className="text-sm text-gray-500">
            No competition teams yet. Create one to start picking a roster.
          </p>
        </div>
      )}
    </div>
  );
}
